import React, { ReactNode, useMemo } from 'react';
import { useTheme, Box, Typography } from '@mui/material';
import { useTranslation } from "react-i18next";
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import withQueryWidget, { WithQueryWidgetProps } from 'components/widgetParts/queryWidgetComposition/withQueryWidget';
import { getSingleResponseValue, getWidgetResponsesByName, widgetQueryTitleByResponses, WidgetResponseItem } from 'system/widget/Widget';
import { defaultSettings } from './ShareOfVoiceSettings';
import HorizontalBar from 'components/charting/HorizontalBar';
import TitleCase from 'components/formatting/TitleCase';
import ISOPercentage from 'components/formatting/ISOPercentage';
import { Query } from 'system/query/Query';
import ShareOfVoiceTooltip from './ShareOfVoiceTooltip';
import ISONumber from 'components/formatting/ISONumber';

function ShareOfVoiceContent(props: WithQueryWidgetProps): JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const [drilldownTitle, setDrilldownTitle] = React.useState<string | null>(null);
  const [drilldownQuery, setDrilldownQuery] = React.useState<Query | null>(null);
  const [drilldownQueryIds, setDrilldownQueryIds] = React.useState<string[]>([]);

  const currentState = {...defaultSettings, ...props.widget?.data?.settings};

  const displayRows: ReactNode[] = useMemo(() => {
    let apiResponses = getWidgetResponsesByName(props.apiResponses, "share-of-voice");
    const rows: ReactNode[] = [];

    if (apiResponses && !Array.isArray(apiResponses)) {
      apiResponses = [apiResponses];
    }

    if (Array.isArray(apiResponses) && apiResponses.length > 0) {
      const sortedResponses = [...apiResponses];

      const total = sortedResponses.reduce((sum, response) => {
        return sum + getSingleResponseValue(response);
      }, 0);

      // We need to be able to sort by either count or percentage, based on settings
      const metricForResponse = (response: any) => {
        const value = getSingleResponseValue(response);
        const percent = total > 0 ? value / total : 0;
        return currentState.displayValue === "count" ? value : percent;
      };

      // Sort responses based on settings
      switch (currentState.sortValue) {
        case "asc":
          sortedResponses.sort((a, b) => {
            const diff = metricForResponse(a) - metricForResponse(b);
            if (diff !== 0) return diff;
            return (a.queryDisplayTitle || "").localeCompare(b.queryDisplayTitle || "");
          });
          break;
        case "desc":
          sortedResponses.sort((a, b) => {
            const diff = metricForResponse(b) - metricForResponse(a);
            if (diff !== 0) return diff;
            return (a.queryDisplayTitle || "").localeCompare(b.queryDisplayTitle || "");
          });
          break;
        case "none":
        default:
          break;
      }

      sortedResponses.forEach((response, index) => {
        const activeRequest = response.originatingRequest;
        const responseBody = response.responseBody as WidgetResponseItem;
        const keys = Object.keys(responseBody);
        const rowTitle = response.queryDisplayTitle;
        const rowValue = getSingleResponseValue(response);
        const rowPercent = (total > 0) ? (rowValue / total) : 0;
        let rowLabel = keys.find((key) => key !== "_total") || i18n.t("records");
        let rowSubLabel = "";

        // If one of the two keys is an object, save the value from the labels array
        if (keys.length === 2 && typeof responseBody[rowLabel] === "object") {
          const subKeys = responseBody[rowLabel].labels;
          if (subKeys.length > 0) {
            rowSubLabel = subKeys[0];
          }
        }

        // If the label is "social_universe", replace it with the translation
        if (rowLabel === "social_universe") {
          rowLabel = i18n.t("social_universe");
        }

        const drilldown = () => {
          setDrilldownQuery(null);
          setDrilldownTitle(rowTitle);
          setDrilldownQueryIds(activeRequest.activeQueryIds);
        }

        const bar = {
          color: activeRequest.queryRequestColor || theme.palette.primary.main,
          count: rowValue,
          drilldownQuery: null,
          key: rowTitle,
          label: rowTitle,
          category_label: rowLabel,
          subCategory_label: rowSubLabel,
          percent: rowPercent,
          value: total
        };

        // Each row is a category with a percentage bar.
        rows.push(
          <Box
            key={`${response.queryDisplayTitle || "query"}-${index}`}
            sx={{cursor: "pointer"}}
            onClick={drilldown}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="subtitle2">
                  <TitleCase text={rowTitle} />
                </Typography>
                <Box sx={{ ml: 1}}>
                  {currentState.displayValue === "percent" && <ISOPercentage value={rowPercent} />}
                  {currentState.displayValue === "count" && <ISONumber number={rowValue} />}
                </Box>
              </Box>
            <HorizontalBar
              color={bar.color}
              percentage={rowPercent ?? 0}
              sx={{mb:2}}
              bar={bar}
              tooltip={(bar) => <ShareOfVoiceTooltip bar={bar} />}
            />
          </Box>
        );
      });
    }
    return rows;
  }, [theme.palette.primary.main, props.apiResponses]);

  const widgetQueryTitle = widgetQueryTitleByResponses(props.apiResponses, "share-of-voice", i18n);

  return (
    <>
      {displayRows.length > 0 &&
        <WidgetLayout
          header={
            <WidgetTitle
              alignment={currentState.widgetTitleAlignment}
              customTitle={currentState.customWidgetTitle}
              defaultTitle={i18n.t('widget-types.share-of-voice.label')}
              settingsTitle={currentState.widgetTitle}
              widgetQueryTitle={widgetQueryTitle}
              />
          }
        >
          <Box className="drag-cancel" sx={{overflowY: "auto", p: 2, pt: 1}}>{displayRows}</Box>
        </WidgetLayout>
      }
      {!displayRows?.length &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
    </>
  );
}

export default withQueryWidget(ShareOfVoiceContent, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});
